/**
 * ============================================================================
 * 🔧 Server Data Service — User, Statistik, Suchen, Favoriten, etc.
 * ============================================================================
 * 
 * Sammelt alle kleineren Server-Services in einer Datei:
 * - User-Profil
 * - Statistiken
 * - Letzte Suchen / Live-Suche
 * - Favoriten
 * - Warteliste
 * - Backups
 * - Premium
 * ============================================================================
 */

import { api } from './apiService';

// ============================================================
// User
// ============================================================

/**
 * User-Dokument laden
 */
export async function getUser() {
  return api.get('/user');
}

/**
 * User-Dokument aktualisieren
 * @param {Object} data - { packagesHash, lastSync, displayName, ... }
 */
export async function updateUser(data) {
  return api.put('/user', data);
}

/**
 * Sync-Hash laden (optimiert — nur Hash + lastSync)
 */
export async function getSyncHash() {
  return api.get('/user/sync-hash');
}

// ============================================================
// Statistiken
// ============================================================
// EINZIGE Quelle: /statistics/overview (serverseitig atomar gepflegte
// statistics_daily-Tabelle). Die alten Client-gepflegten Blobs
// (summary/monthly) sind abgeschafft — der Server zählt selbst an den
// Paket-Zustandsübergängen.

/**
 * Statistik-Übersicht laden (Totals, Heute, Lagernd, Ø Lagerdauer,
 * 12 Monate Tageswerte, Stunden-Histogramm)
 */
export async function getStatisticsOverview() {
  return api.get('/statistics/overview');
}

/**
 * Statistik komplett zurücksetzen — wirkt serverseitig, also auf allen Geräten
 */
export async function resetStatistics() {
  return api.delete('/statistics');
}

/**
 * 💶 Optionale Provisions-Einstellungen je Paketdienst serverseitig speichern
 */
export async function saveStatisticsCommission(commissionConfig) {
  return api.put('/statistics/summary', { commissionConfig });
}

// ============================================================
// Suchen
// ============================================================

/**
 * Letzte Suchen laden
 */
export async function getRecentSearches() {
  return api.get('/searches/recent');
}

/**
 * Letzte Suchen speichern
 * @param {string} lastSearch - Letzte Suche
 * @param {Array} recentSearches - Suchverlauf
 */
export async function saveRecentSearches(lastSearch, recentSearches) {
  return api.put('/searches/recent', { lastSearch, recentSearches });
}

/**
 * Live-Suche senden (Tablet → PC via SSE)
 * @param {string} query - Suchbegriff
 */
export async function sendLiveSearch(query) {
  return api.put('/searches/live', {
    query,
    timestamp: new Date().toISOString(),
  });
}

// ============================================================
// Favoriten
// ============================================================

/**
 * Alle Favoriten laden
 */
export async function getFavoriten() {
  const result = await api.get('/favoriten');
  return result.favoriten || [];
}

/**
 * Neuen Favoriten anlegen
 * @param {Object} favorit - { name, adresse, notizen }
 */
export async function createFavorit(favorit) {
  return api.post('/favoriten', favorit);
}

/**
 * Favoriten aktualisieren
 * @param {number} id
 * @param {Object} updates
 */
export async function updateFavorit(id, updates) {
  return api.patch(`/favoriten/${id}`, updates);
}

/**
 * Favoriten löschen
 * @param {number} id
 */
export async function deleteFavorit(id) {
  return api.delete(`/favoriten/${id}`);
}

// ============================================================
// Warteliste
// ============================================================

/**
 * Alle aktiven Wartelisten-Einträge laden
 */
export async function getWaitingList() {
  const result = await api.get('/waiting-list');
  return result.waitingList || [];
}

/**
 * Passende Wartelisten-Einträge suchen
 * Hinweis: Bei E2E-Verschlüsselung matcht der Server nur auf verschlüsselten Werten.
 * Matching muss client-seitig auf entschlüsselten Daten passieren.
 * @param {string} [name]
 * @param {string} [strasse]
 */
export async function matchWaitingList(name, strasse) {
  const result = await api.get('/waiting-list/match', { name, strasse });
  return result.matches || [];
}

/**
 * Wartelisten-Eintrag erstellen/aktualisieren
 * @param {string} id - Firebase-ID
 * @param {Object} entry - { name, strasse, notizen, active }
 */
export async function saveWaitingListEntry(id, entry) {
  return api.put(`/waiting-list/${id}`, entry);
}

/**
 * Wartelisten-Eintrag teilweise aktualisieren
 * @param {string} id
 * @param {Object} updates - { benachrichtigt, active }
 */
export async function patchWaitingListEntry(id, updates) {
  return api.patch(`/waiting-list/${id}`, updates);
}

/**
 * Wartelisten-Eintrag löschen/deaktivieren
 * @param {string|number} id
 */
export async function deleteWaitingListEntry(id) {
  return api.delete(`/waiting-list/${id}`);
}

/**
 * Signierten QR-Link für die Selbst-Eintragung auf dem Kunden-Smartphone abrufen
 */
export async function getWaitingListQrToken() {
  return api.get('/waiting-list/qr-token');
}

/**
 * Benachrichtigung an Kunden senden
 * @param {Object} customer - { name, kontakt, strasse, hausnummer }
 * @param {Object} paket - { name, streetAndNumber, regalplatz }
 * @param {string} kontaktTyp - 'email'
 */
export async function sendNotification(customer, paket, kontaktTyp = 'email', extra = {}) {
  return api.post('/notify', { customer, paket, kontaktTyp, ...extra });
}

// ============================================================
// Backups
// ============================================================

/**
 * Alle Backups auflisten
 */
export async function listBackups() {
  const result = await api.get('/backups');
  return result.backups || [];
}

/**
 * Neues Backup erstellen
 * @param {Object} data - Backup-Daten
 * @param {string} [filename] - Optionaler Dateiname
 */
export async function createBackup(data, filename) {
  return api.post('/backups', { data, filename });
}

/**
 * Backup herunterladen
 * @param {string} filename
 */
export async function downloadBackup(filename) {
  return api.get(`/backups/${encodeURIComponent(filename)}`);
}

/**
 * Backup löschen
 * @param {string} filename
 */
export async function deleteBackup(filename) {
  return api.delete(`/backups/${encodeURIComponent(filename)}`);
}

// ============================================================
// Premium
// ============================================================

/**
 * Premium-Status laden
 */
export async function getPremiumStatus() {
  return api.get('/premium/status');
}

/**
 * Trial starten
 * @param {string} trialEndsAt - ISO Datum
 */
export async function startTrial(trialEndsAt) {
  return api.put('/premium/trial', { trialEndsAt });
}

/**
 * Kauf speichern
 * @param {Object} purchaseData
 */
export async function savePurchase(purchaseData) {
  return api.put('/premium/purchase', { purchaseData });
}

/**
 * 🔒 Abo-Kauf SERVERSEITIG bei Google prüfen lassen (#2-purchase).
 * Schickt nur den Kauf-Token; der Server fragt Google und schaltet mit echtem Ablaufdatum frei.
 * @param {{purchaseToken: string, productId?: string}} payload
 */
export async function verifyPurchase(payload) {
  return api.put('/premium/verify', payload);
}

/**
 * 🧭 Konto-Profil (Setup) speichern — gilt für alle Geräte des Kontos.
 * @param {{replace?: boolean, settings?: object, features?: object, carriers?: string[]}} patch
 */
export async function saveAccountProfile(patch) {
  return api.put('/user/profile', patch);
}

/** 🔑 Profil-Code des eigenen Kontos holen bzw. anlegen (neu=true → neuen Code erzeugen, der alte gilt nicht mehr) */
export async function createProfileCode(neu = false) {
  return api.post('/user/profile/code', { neu: !!neu });
}

/** 🔑 Teilen beenden: der Code gilt danach nicht mehr */
export async function deleteProfileCode() {
  return api.delete('/user/profile/code');
}

/** 🔑 Profil eines anderen Kontos per Code laden (Kopie von Einstellungen, Funktionen, Paketdiensten) */
export async function fetchSharedProfile(code) {
  const c = String(code || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  return api.get(`/user/profile/shared/${encodeURIComponent(c)}`);
}

/**
 * Alle User-Daten löschen (DSGVO)
 */
export async function deleteAllUserData() {
  return api.delete('/premium/data');
}

// ============================================================
// Auth (Profil / Sessions)
// ============================================================

/**
 * Aktuellen User + Drive-Status laden
 */
export async function getMe() {
  return api.get('/auth/me');
}

/**
 * Aktive Sessions auflisten
 */
export async function getSessions() {
  const result = await api.get('/auth/sessions');
  return result.sessions || [];
}

/**
 * Einzelne Session beenden
 * @param {number} sessionId
 */
export async function revokeSession(sessionId) {
  return api.delete(`/auth/sessions/${sessionId}`);
}

/**
 * Alle Sessions beenden
 */
export async function logoutAll() {
  return api.post('/auth/logout-all');
}
